import { useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@clerk/clerk-react'
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai/conversation'
import { Skeleton } from '@/components/ui/skeleton'
import { useConversationContext } from '@/contexts/ConversationContext'
import { useConversationNav } from '@/hooks/useConversationNav'
import {
  useConversationMessages,
  generateConversationId,
  isConversationNotFoundError,
} from '@/hooks/useConversationsQuery'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useMountEffect } from '@/hooks/useMountEffect'
import { ApiError } from '@/lib/api'
import {
  attachmentRejection,
  isStorageBackedAttachment,
  isStorageBackedFile,
  MAX_ATTACHMENTS,
  toPendingAttachment,
  type PendingAttachment,
} from '@/lib/attachments'
import { useMessageQueue, type QueuedMessage } from '@/stores/messageQueue'
import type { ChatEvent, MessageIntent } from '@/types'

import { AgentChatInput } from './AgentChatInput'
import {
  CLARIFYING_TOOL_NAME,
  ClarifyingQuestionMessage,
  isClarifyingResolved,
} from './ClarifyingQuestionMessage'
import { ConversationLoadError } from './ConversationLoadError'
import { EmptyState } from './EmptyState'
import { EmptyStateSuggestions } from './EmptyStateSuggestions'
import { groupEvents } from './groupEvents'
import { ThinkingIndicator } from './ThinkingIndicator'
import { useQueueDrain } from './useQueueDrain'
import { renderItem } from './utils'
import { toast } from 'sonner'
import { conversationKeys, notificationKeys } from '@/lib/queryKeys'
import {
  changedDutyAssignment,
  useRefreshDutyAssignment,
} from '@/features/duties/hooks/useRefreshDutyAssignment'
import { Alert, AlertDescription } from '@/components/ui/alert'

// Stable identity so persistedItems isn't regrouped on every render while the
// conversation has no data yet.
const EMPTY_EVENTS: ChatEvent[] = []
const STEER_NO_ATTACHMENTS =
  'Attachments cannot be sent mid-task yet. Queue the message instead.'

export function AgentChat() {
  const inputKey = useRef<string>('')
  const isSendingRef = useRef(false)
  // Count of persisted user messages at the moment a turn is sent. After the
  // stream ends we keep the optimistic turn on screen until the post-stream
  // refetch grows the persisted count past this baseline, then hand off. The
  // turn can't be identified by content (a repeated identical question collides
  // with an earlier bubble) nor by id/timestamp (the server re-mints both on
  // every history read), so the count is the only reliable "it's persisted now"
  // signal.
  const persistedUserCountAtSendRef = useRef<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const posthog = usePostHog()
  const queryClient = useQueryClient()
  const refreshDutyAssignment = useRefreshDutyAssignment()
  const { configuration } = useConfiguration()
  const { goToConversation } = useConversationNav()

  const {
    agentId,
    events: streamingEvents,
    eventsThreadId,
    clearEvents,
    sendMessage,
    steer,
    submitClarifyingAnswer,
    clarifyingAnswers,
    streamingState,
    runFailed,
    stop,
    detach,
  } = useConversationContext()

  const suggestions = useMemo(
    () => configuration?.agents?.[agentId]?.suggested_prompts ?? [],
    [configuration, agentId]
  )
  const conversationId = searchParams.get('conversationId') || undefined
  const { organization } = useOrganization()
  const orgSlug = organization?.slug

  // True only when a stream is in flight AND its events belong to the
  // conversation currently on screen. Gates every user-visible "something is
  // streaming" affordance so a stream in A never disables/hides controls in B.
  const isMyConversationStreaming =
    streamingState.isStreaming && eventsThreadId === conversationId

  const {
    data: conversationData,
    isLoading: isLoadingMessages,
    isError: isConversationError,
    error: conversationError,
    refetch: refetchMessages,
  } = useConversationMessages(agentId, conversationId, {
    // This tab already watches the live stream — polling would only
    // re-download unchanged history.
    suspendPolling: isMyConversationStreaming,
  })
  const persistedMessages = conversationData?.events ?? EMPTY_EVENTS

  const totalMessages = persistedMessages.length + streamingEvents.length

  // Fold runs of thinking + tool calls into work blocks (David only — other
  // agents render flat, see groupEvents). Both render paths go through the
  // same pass, so persisted history and the live stream group identically;
  // the underlying event lists stay flat.
  const persistedItems = useMemo(
    () => groupEvents(persistedMessages, agentId),
    [persistedMessages, agentId]
  )
  const streamingItems = useMemo(
    () => groupEvents(streamingEvents, agentId),
    [streamingEvents, agentId]
  )

  // The detached run for this conversation is working server-side but this tab
  // has no live stream for it (page reload, or switched away and back). The
  // messages query polls until it goes terminal.
  const isWorkingServerSide =
    conversationData?.runStatus === 'running' && !isMyConversationStreaming
  // Either way the conversation is busy: gates sending, the empty state, the
  // thinking indicator and the composer's stop affordance.
  const isRunActive = isMyConversationStreaming || isWorkingServerSide

  const showStreamingEvents = useMemo(() => {
    if (eventsThreadId !== conversationId) return false
    if (streamingEvents.length === 0) return false
    if (streamingState.isStreaming) return true
    // Stream finished: keep the optimistic turn visible until the post-stream
    // refetch has actually persisted it, then hand off to persistedMessages.
    const lastStreamedUserMsg = streamingEvents
      .slice()
      .toReversed()
      .find((e) => e.type === 'user_message')
    if (!lastStreamedUserMsg) return false
    const baseline = persistedUserCountAtSendRef.current
    if (baseline === null) return false
    const persistedUserCount = persistedMessages.filter(
      (e) => e.type === 'user_message'
    ).length
    if (persistedUserCount <= baseline) return true
    // A steer adds a second optimistic bubble and both land in one save. If
    // only part landed, the run was cut short: keep showing them while the
    // server still works, since the rest may yet land.
    const optimisticUserCount = streamingEvents.filter(
      (e) => e.type === 'user_message'
    ).length
    if (persistedUserCount >= baseline + optimisticUserCount) return false
    return conversationData?.runStatus === 'running'
  }, [
    eventsThreadId,
    conversationId,
    streamingEvents,
    streamingState.isStreaming,
    persistedMessages,
    conversationData?.runStatus,
  ])

  // The stream's last block stays live through prose after it: more work can
  // still fold that prose in. A later user message starts a new turn.
  const lastBlockOrUserIndex = streamingItems.findLastIndex(
    (item) => item.type === 'work_block' || item.type === 'user_message'
  )
  const liveBlockIndex =
    isMyConversationStreaming &&
    showStreamingEvents &&
    streamingItems[lastBlockOrUserIndex]?.type === 'work_block'
      ? lastBlockOrUserIndex
      : -1

  // The active clarifying question (if any) floats above the composer instead
  // of rendering inline. Only the call that ended the turn can still be
  // answered (the server honours the last response alone, and a mid-run steer
  // already dismissed it), so an older call without a result gets no card.
  const activeClarifyingEvent = useMemo(() => {
    if (isRunActive) return undefined
    const last = (showStreamingEvents ? streamingEvents : persistedMessages).at(
      -1
    )
    return last?.type === 'tool_call' &&
      last.toolName === CLARIFYING_TOOL_NAME &&
      last.toolCallId &&
      !isClarifyingResolved(last, clarifyingAnswers)
      ? last
      : undefined
  }, [
    isRunActive,
    persistedMessages,
    showStreamingEvents,
    streamingEvents,
    clarifyingAnswers,
  ])

  // Lives here, not in AgentChatInput: the composer is remounted after every
  // send (key={inputKey.current}), which would wipe state held inside it.
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])

  // Mirrors `attachments` so handlers and effects can read the live list
  // without depending on it — a captured copy goes stale between batched
  // updates and resurrects chips the user already removed.
  const attachmentsRef = useRef<PendingAttachment[]>([])
  attachmentsRef.current = attachments
  const sentUrlsRef = useRef<string[]>([])

  const discardAttachments = useCallback(() => {
    attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.url))
    setAttachments([])
  }, [])

  useMountEffect(() => () => {
    attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.url))
    sentUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
  })

  const handleAddFiles = useCallback(
    (files: File[]) => {
      // Validation, toasts and createObjectURL all stay outside the updater:
      // StrictMode double-invokes it, which would double-toast and leak a URL.
      const current = attachmentsRef.current
      const accepted: File[] = []
      let usedBytes = current.reduce(
        (sum, attachment) =>
          isStorageBackedAttachment(attachment)
            ? sum
            : sum + attachment.file.size,
        0
      )
      for (const file of files) {
        const rejection = attachmentRejection(
          file,
          usedBytes,
          agentId === 'david'
        )
        if (rejection) {
          toast.error(rejection)
          continue
        }
        if (!isStorageBackedFile(file)) usedBytes += file.size
        accepted.push(file)
      }
      const room = MAX_ATTACHMENTS - current.length
      if (accepted.length > room) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files.`)
      }
      if (room <= 0 || accepted.length === 0) return
      const added = accepted.slice(0, room).map(toPendingAttachment)
      setAttachments((live) => [...live, ...added])
    },
    [agentId]
  )

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((current) => {
      const dropped = current.find((a) => a.id === id)
      if (dropped) URL.revokeObjectURL(dropped.url)
      return current.filter((a) => a.id !== id)
    })
  }, [])

  const prevAgentIdRef = useRef(agentId)
  useEffect(() => {
    if (prevAgentIdRef.current === agentId) return
    prevAgentIdRef.current = agentId
    if (agentId === 'david') return

    const dataFiles = attachmentsRef.current.filter(isStorageBackedAttachment)
    if (dataFiles.length === 0) return
    dataFiles.forEach((attachment) => URL.revokeObjectURL(attachment.url))
    setAttachments((current) =>
      current.filter((attachment) => !isStorageBackedAttachment(attachment))
    )
  }, [agentId])

  const prevConversationIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      if (prevConversationIdRef.current !== undefined) {
        // Stop watching only — the detached run keeps working server-side.
        // The streaming→idle effect below refetches the list, whose 'running'
        // row is what arms the sidebar polling/badge for the thread we left;
        // the thread's own messages query is always stale (staleTime 0), so
        // switching back refetches it without an invalidation here.
        if (streamingState.isStreaming) detach()
        clearEvents()
        // Staged files belong to the thread they were picked in.
        discardAttachments()
      }
      prevConversationIdRef.current = conversationId
    }
  }, [
    conversationId,
    clearEvents,
    discardAttachments,
    detach,
    streamingState.isStreaming,
  ])

  // Error-recovery effect. Stays inline (documented exception in
  // useConversationNav.ts). Only a real "conversation not found" (backend 404)
  // strips the URL param; transient failures (network blip, 502, stale-token
  // 500) keep the user on the URL so a Retry can recover in place. The
  // eventsThreadId guard is critical: a brand-new conversation 404s on its
  // initial history GET (we navigate before the row is persisted), and there
  // eventsThreadId === conversationId, so we must NOT redirect — the post-stream
  // invalidation effect below refetches and clears the error once the row exists.
  const errorReportedRef = useRef(false)
  useEffect(() => {
    if (!isConversationError) {
      errorReportedRef.current = false
      return
    }
    // A brand-new conversation 404s on its initial history GET (we navigate
    // before the row is persisted). That's expected — not a load failure and
    // not a reason to redirect or emit analytics; the post-stream invalidation
    // effect below refetches and clears the error once the row exists.
    const isNewConversationRace =
      isConversationNotFoundError(conversationError) &&
      eventsThreadId === conversationId
    if (isNewConversationRace) return

    if (!errorReportedRef.current) {
      errorReportedRef.current = true
      posthog?.capture('conversation_history_load_failed', {
        agent: agentId,
        status:
          conversationError instanceof ApiError
            ? conversationError.status
            : undefined,
        online: navigator.onLine,
      })
    }
    if (streamingState.isStreaming) return
    if (!isConversationNotFoundError(conversationError)) return
    const params = new URLSearchParams(searchParams)
    if (params.has('conversationId')) {
      params.delete('conversationId')
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname || `/${agentId}`,
          search: search ? `?${search}` : '',
        },
        { replace: true }
      )
    }
  }, [
    isConversationError,
    conversationError,
    eventsThreadId,
    conversationId,
    agentId,
    posthog,
    navigate,
    location.pathname,
    searchParams,
    streamingState.isStreaming,
  ])

  const wasStreamingRef = useRef<boolean>(false)
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current
    if (wasStreaming && !streamingState.isStreaming) {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.list(agentId, orgSlug),
      })
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: conversationKeys.messagesAll(agentId, conversationId),
        })
      }
      // A finished David turn may have published a notification. Invalidating
      // here makes the badge move immediately instead of on the next poll.
      if (organization?.id) {
        queryClient.invalidateQueries({
          queryKey: notificationKeys.all(organization.id),
        })
      }
      // If it saved or applied a duty's rules, any open duty page shows the
      // new rules, members and last changes without a reload. Other turns
      // skip it: the refetch reloads every client page already loaded.
      if (changedDutyAssignment(streamingEvents)) void refreshDutyAssignment()
    }
    wasStreamingRef.current = streamingState.isStreaming
  }, [
    streamingState.isStreaming,
    streamingEvents,
    queryClient,
    agentId,
    orgSlug,
    organization?.id,
    conversationId,
    refreshDutyAssignment,
  ])

  const startTurn = useCallback(
    async (
      messageText: string,
      files: PendingAttachment[],
      intent?: MessageIntent
    ) => {
      const onBaseRoute = !conversationId
      const targetId = (
        onBaseRoute ? generateConversationId() : conversationId
      ) as string
      if (onBaseRoute) {
        // replace:false so back-button returns to the empty-state /agent.
        goToConversation(agentId, targetId, false)
      }
      // Snapshot persisted user-message count so showStreamingEvents can keep
      // this turn on screen until the refetch persists it (see the ref above).
      persistedUserCountAtSendRef.current = persistedMessages.filter(
        (e) => e.type === 'user_message'
      ).length
      const result = await sendMessage(messageText, targetId, {
        attachments: files,
        intent,
      })
      if (result === 'aborted' || result === 'failed') return result
      // This turn's object URLs stay alive because the optimistic bubble
      // renders the thumbnails from them until the post-stream refetch
      // replaces it; the previous turn's are past that handoff.
      sentUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      sentUrlsRef.current = files.map((a) => a.url)
      posthog?.capture('message_sent', {
        agent: agentId,
        length: messageText.length,
      })
      return result
    },
    [
      conversationId,
      agentId,
      goToConversation,
      persistedMessages,
      sendMessage,
      posthog,
    ]
  )

  const sendQueued = useCallback(
    (item: QueuedMessage) => startTurn(item.text, item.attachments, 'queued'),
    [startTurn]
  )
  // A promoted item is out of the queue while its steer is in flight. If the
  // steer falls back, the item behind it must not have gone first.
  const [promoting, setPromoting] = useState(false)
  useQueueDrain({
    conversationId,
    // An open clarifying card ends the run too; a drained item would be
    // taken as its answer.
    enabled:
      agentId === 'david' &&
      !isLoadingMessages &&
      !activeClarifyingEvent &&
      !promoting,
    isRunActive,
    runFailed,
    runStatus: conversationData?.runStatus,
    send: sendQueued,
  })

  const hold = useMessageQueue((s) => s.hold)
  const enqueue = useMessageQueue((s) => s.enqueue)
  const removeQueued = useMessageQueue((s) => s.remove)

  const handleQueue = useCallback(
    (text: string) => {
      if (!conversationId) return
      // The queue now owns these files and their object URLs.
      enqueue(conversationId, text, attachmentsRef.current)
      setAttachments([])
    },
    [conversationId, enqueue]
  )

  const handleSteer = useCallback(
    (text: string) => {
      if (attachmentsRef.current.length > 0) {
        toast.error(STEER_NO_ATTACHMENTS)
        return false
      }
      if (conversationId && steer) void steer(text, conversationId)
      return true
    },
    [conversationId, steer]
  )

  const handlePromote = useCallback(
    (item: QueuedMessage) => {
      if (!conversationId || !steer) return
      if (item.attachments.length > 0) {
        toast.error(STEER_NO_ATTACHMENTS)
        return
      }
      const index = useMessageQueue
        .getState()
        .queues[conversationId]?.items.findIndex((i) => i.id === item.id)
      // Out of the queue before the await, so a drain cannot also send it. A
      // steer that falls back puts it back at the same position.
      removeQueued(conversationId, item.id)
      setPromoting(true)
      void steer(item.text, conversationId, index).finally(() =>
        setPromoting(false)
      )
    },
    [conversationId, removeQueued, steer]
  )

  const followUp = useMemo(
    () =>
      agentId === 'david' && conversationId
        ? {
            conversationId,
            onQueue: handleQueue,
            onSteer: handleSteer,
            onPromote: handlePromote,
          }
        : undefined,
    [agentId, conversationId, handleQueue, handleSteer, handlePromote]
  )

  const handleStop = useCallback(() => {
    // Stop holds the queue so the user decides what still applies.
    if (conversationId) hold(conversationId)
    stop(conversationId)
  }, [conversationId, hold, stop])

  const handleSendMessage = useCallback(
    async (messageText: string) => {
      // An attachment with no text is a legitimate turn ("look at this").
      if (
        (!messageText.trim() && attachments.length === 0) ||
        isRunActive ||
        isSendingRef.current
      )
        return
      isSendingRef.current = true
      try {
        inputKey.current = `${Date.now()}`
        // With a clarifying card open, the composer answers it directly: the
        // typed message becomes the deferred tool result and dismisses the card.
        if (activeClarifyingEvent?.toolCallId && conversationId) {
          // This path answers the open card instead of sending a message, and
          // has nowhere to put files. Refuse rather than drop them silently.
          if (attachments.length > 0) {
            toast.error('Answer the question first, then attach files.')
            return
          }
          await submitClarifyingAnswer(
            activeClarifyingEvent.toolCallId,
            { answers: {}, reply: messageText },
            conversationId
          )
          return
        }
        // Clear before awaiting the stream: the optimistic bubble already shows
        // these files, so keeping them in the composer shows every attachment
        // twice for the whole run. Anything short of a completed send puts them
        // back — a stopped turn may never persist, and a failed upload never
        // sent them at all, so the user would otherwise be left with an empty
        // composer and a file they still need to swap out.
        const sent = attachments
        setAttachments([])
        const result = await startTurn(messageText, sent)
        if (result === 'aborted' || result === 'failed') setAttachments(sent)
      } finally {
        isSendingRef.current = false
      }
    },
    [
      isRunActive,
      conversationId,
      submitClarifyingAnswer,
      activeClarifyingEvent,
      attachments,
      startTurn,
    ]
  )

  const isEmpty = !isLoadingMessages && totalMessages === 0 && !isRunActive

  // Transient (non-404) load failure with nothing already on screen: keep the
  // user on the URL and offer a Retry instead of the misleading empty state.
  // 404s are handled by the redirect effect above; a streaming thread hides the
  // error entirely. `totalMessages === 0` guard: React Query keeps last-good
  // data on a background-refetch error, so if we already have messages we keep
  // showing them rather than nuking the view for a transient blip.
  const showLoadError =
    isConversationError &&
    !isConversationNotFoundError(conversationError) &&
    !isMyConversationStreaming &&
    totalMessages === 0

  return (
    <div className="flex flex-col h-full min-h-0 max-w-screen">
      {showLoadError ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <ConversationLoadError onRetry={() => refetchMessages()} />
        </div>
      ) : isEmpty ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="grow shrink-0 flex items-center justify-center px-4">
            <EmptyState />
          </div>
          <div className="min-h-0 shrink overflow-y-auto pt-4">
            <EmptyStateSuggestions
              suggestions={suggestions}
              onSuggestionClick={handleSendMessage}
            />
          </div>
        </div>
      ) : (
        <Conversation className="flex-1">
          <ConversationContent className="gap-3">
            {isLoadingMessages && totalMessages === 0 ? (
              <div className="flex flex-col py-16 space-y-3 max-w-4xl">
                <Skeleton className="h-4 w-3/4 max-w-lg" />
                <Skeleton className="h-4 w-5/6 max-w-xl" />
                <Skeleton className="h-4 w-2/3 max-w-md" />
                <Skeleton className="h-4 w-4/5 max-w-lg" />
                <Skeleton className="h-4 w-1/2 max-w-sm" />
              </div>
            ) : (
              <>
                {persistedItems.map((item) => renderItem(item, agentId))}
                {showStreamingEvents &&
                  streamingItems.map((item, index) =>
                    renderItem(
                      item,
                      agentId,
                      index === liveBlockIndex ? 'live' : 'done'
                    )
                  )}
              </>
            )}
            {isRunActive && liveBlockIndex === -1 && (
              <ThinkingIndicator agentId={agentId} />
            )}
            {conversationData?.runStatus === 'failed' &&
              !isMyConversationStreaming && (
                <Alert>
                  <AlertDescription>
                    The last run stopped before it finished. Send your message
                    again to continue.
                  </AlertDescription>
                </Alert>
              )}
          </ConversationContent>
          <ConversationScrollButton
            variant="default"
            className="bg-david-solid hover:bg-david-hover-solid"
          />
        </Conversation>
      )}
      {activeClarifyingEvent && (
        <div className="flex justify-center px-4 pt-2">
          <ClarifyingQuestionMessage
            event={activeClarifyingEvent}
            placement="floating"
          />
        </div>
      )}
      {!showLoadError && (
        <AgentChatInput
          attachments={attachments}
          attachmentsDisabled={Boolean(activeClarifyingEvent?.toolCallId)}
          handleSendMessage={handleSendMessage}
          onAddFiles={handleAddFiles}
          onRemoveAttachment={handleRemoveAttachment}
          // Per conversation too, so a draft or a queued-message edit cannot
          // follow the user into another thread.
          key={`${conversationId}:${inputKey.current}`}
          // Only the post-send remount refocuses; the panel must not steal
          // focus from the page on load or on a conversation switch.
          autoFocus={inputKey.current !== ''}
          isStreaming={isRunActive}
          onStop={handleStop}
          followUp={followUp}
          placeholder={activeClarifyingEvent ? 'Or reply directly…' : undefined}
        />
      )}
    </div>
  )
}
